import { apiClient } from './client'
import type {
  ReportSummary,
  ReportPeriod,
  ExportRequest,
  ExportJob,
  MetricSummary,
  ReportInsight,
} from '@/types'

export interface ReportSummaryParams {
  period?: ReportPeriod
  member_id?: string
}

interface SummaryResponse {
  generated_at: string
  health_summary: { total_data_points: number; metrics_by_type: Record<string, { average: number | null; minimum: number; maximum: number }> }
  financial_summary: { total_transactions: number; spending: { total: number }; income: { total: number }; category_breakdown: Array<{ category: string; total: number; transaction_count: number }>; anomalies_detected: number; flagged_transactions: { count: number } }
  alerts_summary: { total_alerts: number; by_status: Record<string, number>; by_severity: Record<string, number>; by_type: Record<string, number> }
  insights: Array<{ type: ReportInsight['type']; severity: string; message: string; recommendation?: string }>
}

export const reportsApi = {
  // Get summary report
  getSummary: async (familyId: string, params?: ReportSummaryParams): Promise<ReportSummary> => {
    const data = await apiClient.get<SummaryResponse>(`/families/${familyId}/reports/summary`, { params: { period: params?.period } })
    const health = data.health_summary
    const financial = data.financial_summary
    const alerts = data.alerts_summary
    const metrics: Record<string, MetricSummary> = {}
    for (const [name, metric] of Object.entries(health.metrics_by_type)) {
      if (metric.average !== null) metrics[name.toLowerCase()] = { ...metric, average: metric.average }
    }
    return {
      period: params?.period ?? '30d', generated_at: data.generated_at,
      health: { has_data: health.total_data_points > 0, total_data_points: health.total_data_points, metrics },
      financial: {
        has_data: financial.total_transactions > 0, total_transactions: financial.total_transactions,
        total_spending: financial.spending.total, total_income: financial.income.total,
        categories: financial.category_breakdown.map((category) => ({ ...category, amount: category.total, percentage: financial.spending.total ? category.total / financial.spending.total * 100 : 0 })),
        anomalies: { count: financial.anomalies_detected, flagged_count: financial.flagged_transactions.count },
      },
      alerts: {
        total: alerts.total_alerts,
        by_status: { unread: alerts.by_status.UNREAD ?? 0, acknowledged: alerts.by_status.ACKNOWLEDGED ?? 0, resolved: alerts.by_status.RESOLVED ?? 0 },
        by_severity: { low: alerts.by_severity.LOW ?? 0, medium: alerts.by_severity.MEDIUM ?? 0, high: alerts.by_severity.HIGH ?? 0, critical: alerts.by_severity.CRITICAL ?? 0 },
        by_type: { health: alerts.by_type.HEALTH ?? 0, financial: alerts.by_type.FINANCIAL ?? 0, system: alerts.by_type.SYSTEM ?? 0 },
      },
      insights: data.insights.map((insight, i) => ({ id: String(i), type: insight.type, severity: insight.severity === 'critical' ? 'critical' : insight.severity === 'warning' ? 'warning' : 'info', title: insight.message, description: insight.recommendation ?? '', created_at: data.generated_at })),
    }
  },

  // Request an export
  requestExport: (familyId: string, data: ExportRequest) =>
    apiClient.post<ExportJob>(`/families/${familyId}/reports/export`, { ...data, format: data.format.toUpperCase() }),

  // Get export status
  getExportStatus: (_familyId: string, exportId: string) =>
    apiClient.get<ExportJob>(`/reports/${exportId}/status`),

  // Downloads use authenticated requests, including local file responses.
  getExportDownload: (_familyId: string, exportId: string) =>
    apiClient.getBlob(`/reports/${exportId}/download`),
}
